import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LeadScrapeTargeting } from '@/components/LeadScrapeTargetingPanel';
import {
  isEnrichmentProviderId,
  type EnrichmentProviderId,
} from '@/lib/enrichment-providers';
import type { CsvImportDraft } from '@/lib/csv-import';

export type { LeadScrapeTargeting };

export type ChannelId = 'mail' | 'call' | 'formfill' | 'linkedin_outreach';
export type MailProviderKey = 'instantly' | 'successai';
export type EnrichmentProviderKey = EnrichmentProviderId;
// Language is now a free-form DataForSEO language_code (e.g. 'fr', 'en', 'de',
// 'pt', 'zh-CN', 'nb', etc.) — see /v1/dataforseo/languages.
export type Language = string;

export type EmailTemplates = {
  email01: string;
  email02: string;
  email03: string;
  fallback_email01: string;
  fallback_email02: string;
  fallback_email03: string;
};

export type ReplyBrandHandler =
  | 'dispatcher_native'
  | 'getfishnet_r1'
  | 'bigcall_agent_validation'
  | 'manual';

export type MailKnowledgeScope = 'workspace' | 'campaign';
export type MailKnowledgeDocKind = 'faq' | 'objection' | 'product_note' | 'example_reply';
export type MailAssistantPolicyLabel = 'faq_answerable' | 'out_of_office';

export type MailAssistantPolicyDraft = {
  scope: MailKnowledgeScope;
  auto_reply_enabled: boolean;
  auto_reply_labels: MailAssistantPolicyLabel[];
  tone: string;
  escalation_notes: string;
  forbidden_claims: string;
  meeting_booking_instructions: string;
  signature_style: string;
};

export type MailKnowledgeDocDraft = {
  id: string;
  scope: MailKnowledgeScope;
  kind: MailKnowledgeDocKind;
  title: string;
  content_md: string;
};

export type FitGateDraft = {
  brand_scope: string;
  ideal_customer_profile: string;
  anti_profile: string;
  preferred_roles: string;
  preferred_sectors: string;
  preferred_company_types: string;
  blocked_roles: string;
  blocked_organization_types: string;
  blocked_sectors: string;
  blocked_countries: string;
  blocked_languages: string;
  blocked_domains: string;
  blocked_emails: string;
  blocked_companies: string;
  bad_fit_examples: string;
  workspace_suppressions: string;
  brand_suppressions: string;
  campaign_suppressions: string;
  send_threshold: number;
  review_threshold: number;
};

export type WizardState = {
  // W1
  channels_enabled: ChannelId[];
  mail_then_call_fallback: boolean;
  formfill_runs_in_parallel: boolean;
  abandon_if_nothing: boolean;

  // W2 — Sender context
  sender_company_name: string;
  sender_website: string;
  sender_description: string;
  sender_first_name: string;
  sender_role: string;
  sender_email: string;

  // W2 — Offer
  offer_title: string;
  offer_description: string;
  offer_url: string;
  campaign_identifier: string;

  // W2 — Target (DataForSEO Google Ads codes)
  target_country: string;          // ISO 2-letter ('FR', 'BE', …) — display
  target_country_dfs_code: number | null; // DataForSEO location_code (2250 for FR)
  target_city: string;             // free text — DataForSEO doesn't ship cities here
  target_region: string;           // human-readable name
  target_region_dfs_code: number | null;  // DataForSEO Region/State location_code
  language: Language;              // DataForSEO language_code ('fr', 'en', 'pt', …)

  // W4 — Mail providers & accounts
  mail_providers: MailProviderKey[];
  mail_provider_email_account_ids: string[];

  // W4 — LinkedIn Outreach
  outreach_agent_ids: string[];

  // W3 — Email templates
  email_templates: EmailTemplates;
  email_template_style_id: string;

  // W4 — Form-fill message
  form_message_template: string;
  form_message_optimize_per_contact: boolean;

  // W5 — Reply-To forwarder
  reply_to_address: string;
  reply_to_brand_handler: ReplyBrandHandler;

  // W6 — Mail assistant knowledge
  mail_assistant_policy: MailAssistantPolicyDraft;
  mail_assistant_docs: MailKnowledgeDocDraft[];

  // W6 — Sync big6call
  bigcall_workspace_id: string;
  bigcall_campaign_mode: 'create_new' | 'use_existing';
  bigcall_campaign_id: string | null;
  new_bigcall_campaign_name: string;

  // W7 — Enrichisseurs
  enrichment_providers: EnrichmentProviderKey[];
  enrichment_force_complete: boolean;
  leadscrape_targeting: LeadScrapeTargeting;
  csv_import_draft: CsvImportDraft | null;
  fit_gate: FitGateDraft;

  // W8 — Récapitulatif
  campaign_name: string;
  campaign_name_manual_override: boolean;
  mail_provider: string;

  // Setters — W1
  setChannels: (chs: ChannelId[]) => void;
  setMailThenCallFallback: (v: boolean) => void;
  setFormfillRunsInParallel: (v: boolean) => void;
  setAbandonIfNothing: (v: boolean) => void;

  // Setters — W2
  patchW2: (patch: Partial<Omit<WizardState, 'setChannels' | 'setMailThenCallFallback' | 'setFormfillRunsInParallel' | 'setAbandonIfNothing' | 'patchW2' | 'setMailProviders' | 'toggleMailProvider' | 'setMailAccounts' | 'setOutreachAgents' | 'toggleOutreachAgent' | 'patchEmailTemplates' | 'setEmailTemplates' | 'setEmailTemplateStyleId' | 'setFormMessageTemplate' | 'setFormMessageOptimizePerContact' | 'setReplyToAddress' | 'setReplyToBrandHandler' | 'patchMailAssistantPolicy' | 'setMailAssistantDocs' | 'addMailAssistantDoc' | 'updateMailAssistantDoc' | 'removeMailAssistantDoc' | 'setBigcallWorkspaceId' | 'setBigcallCampaignMode' | 'setBigcallCampaignId' | 'setNewBigcallCampaignName' | 'setCampaignName' | 'syncGeneratedCampaignName' | 'useGeneratedCampaignName' | 'setMailProvider' | 'setEnrichmentProviders' | 'toggleEnrichmentProvider' | 'setEnrichmentForceComplete' | 'setLeadScrapeTargeting' | 'setCsvImportDraft' | 'patchFitGate'>>) => void;
  setMailProviders: (providers: MailProviderKey[]) => void;
  toggleMailProvider: (provider: MailProviderKey) => void;
  setMailAccounts: (ids: string[]) => void;
  setOutreachAgents: (ids: string[]) => void;
  toggleOutreachAgent: (id: string) => void;

  // Setters — W3
  patchEmailTemplates: (patch: Partial<EmailTemplates>) => void;
  setEmailTemplates: (next: EmailTemplates) => void;
  setEmailTemplateStyleId: (id: string) => void;

  // Setters — W4
  setFormMessageTemplate: (v: string) => void;
  setFormMessageOptimizePerContact: (v: boolean) => void;

  // Setters — W5
  setReplyToAddress: (v: string) => void;
  setReplyToBrandHandler: (v: ReplyBrandHandler) => void;

  // Setters — W6 mail assistant
  patchMailAssistantPolicy: (patch: Partial<MailAssistantPolicyDraft>) => void;
  setMailAssistantDocs: (docs: MailKnowledgeDocDraft[]) => void;
  addMailAssistantDoc: (doc: MailKnowledgeDocDraft) => void;
  updateMailAssistantDoc: (id: string, patch: Partial<MailKnowledgeDocDraft>) => void;
  removeMailAssistantDoc: (id: string) => void;

  // Setters — W6
  setBigcallWorkspaceId: (v: string) => void;
  setBigcallCampaignMode: (v: 'create_new' | 'use_existing') => void;
  setBigcallCampaignId: (v: string | null) => void;
  setNewBigcallCampaignName: (v: string) => void;

  // Setters — W8
  setCampaignName: (v: string) => void;
  syncGeneratedCampaignName: (v: string) => void;
  useGeneratedCampaignName: (v: string) => void;
  setMailProvider: (v: string) => void;

  // Setters — W7
  setEnrichmentProviders: (v: EnrichmentProviderKey[]) => void;
  toggleEnrichmentProvider: (id: EnrichmentProviderKey) => void;
  setEnrichmentForceComplete: (v: boolean) => void;
  setLeadScrapeTargeting: (v: LeadScrapeTargeting) => void;
  setCsvImportDraft: (v: CsvImportDraft | null) => void;
  patchFitGate: (patch: Partial<FitGateDraft>) => void;
};

function defaultMailAssistantPolicy(): MailAssistantPolicyDraft {
  return {
    scope: 'campaign',
    auto_reply_enabled: false,
    auto_reply_labels: ['faq_answerable', 'out_of_office'],
    tone: 'Sobre, direct, professionnel et chaleureux.',
    escalation_notes:
      'Escalader vers un humain si la demande touche au pricing, a un engagement commercial, a une exception contractuelle, ou si le message reste ambigu.',
    forbidden_claims:
      'Ne rien inventer sur les prix, les delais, les integrations, les chiffres clients, ou les disponibilites non confirmees.',
    meeting_booking_instructions:
      'Quand le lead veut un rendez-vous, proposer un court echange et basculer la finalisation au bon handler.',
    signature_style:
      'Reponse courte, claire, sans push agressif. Garder une fin polie et concrete.',
  };
}

function coerceLegacyMailProvider(providers: MailProviderKey[]): string {
  return providers.length === 1 ? providers[0]! : '';
}

function uniqProviders(values: readonly string[]): MailProviderKey[] {
  return Array.from(new Set(values)).filter(
    (value): value is MailProviderKey => value === 'instantly' || value === 'successai',
  );
}

function uniqEnrichmentProviders(values: readonly string[]): EnrichmentProviderKey[] {
  return Array.from(new Set(values)).filter(isEnrichmentProviderId);
}

export const useWizardStore = create<WizardState>()(
  persist(
    (set) => ({
      // W1
      channels_enabled: ['call'],
      mail_then_call_fallback: false,
      formfill_runs_in_parallel: false,
      abandon_if_nothing: true,

      // W2 — Sender
      sender_company_name: '',
      sender_website: '',
      sender_description: '',
      sender_first_name: '',
      sender_role: '',
      sender_email: '',

      // W2 — Offer
      offer_title: '',
      offer_description: '',
      offer_url: '',
      campaign_identifier: '',

      // W2 — Target
      target_country: '',
      target_country_dfs_code: null,
      target_city: '',
      target_region: '',
      target_region_dfs_code: null,
      language: 'fr',

      // W4 — Mail providers & accounts
      mail_providers: [],
      mail_provider_email_account_ids: [],

      // W4 — LinkedIn Outreach
      outreach_agent_ids: [],

      // W4 — Form-fill message
      form_message_template: '',
      form_message_optimize_per_contact: true,

      // W5 — Reply-To forwarder
      reply_to_address: '',
      reply_to_brand_handler: 'dispatcher_native' as ReplyBrandHandler,

      // W6 — Mail assistant knowledge
      mail_assistant_policy: defaultMailAssistantPolicy(),
      mail_assistant_docs: [],

      // W6 — Sync big6call
      bigcall_workspace_id: '',
      bigcall_campaign_mode: 'create_new' as const,
      bigcall_campaign_id: null,
      new_bigcall_campaign_name: '',

      // W8 — Récapitulatif
      campaign_name: '',
      campaign_name_manual_override: false,
      mail_provider: '',

      // W7 — Enrichisseurs
      enrichment_providers: ['leadscrape'],
      enrichment_force_complete: false,
      csv_import_draft: null,
      leadscrape_targeting: {
        industries: [],
        locations: [],
        selectionFlags: {
          emails_and_contacts: true,
          verify_emails: true,
          reviews_and_ratings: true,
          advertising_tech_stack: false,
          seo_data: false,
        },
      },
      fit_gate: {
        brand_scope: '',
        ideal_customer_profile: '',
        anti_profile: '',
        preferred_roles: '',
        preferred_sectors: '',
        preferred_company_types: '',
        blocked_roles: '',
        blocked_organization_types: '',
        blocked_sectors: '',
        blocked_countries: '',
        blocked_languages: '',
        blocked_domains: '',
        blocked_emails: '',
        blocked_companies: '',
        bad_fit_examples: '',
        workspace_suppressions: '',
        brand_suppressions: '',
        campaign_suppressions: '',
        send_threshold: 0.7,
        review_threshold: 0.4,
      },

      // W3 — Email templates (defaults = "Original" style, Nadir's hand-tuned baseline)
      email_template_style_id: 'original',
      email_templates: {
        email01: `Madame, Monsieur, (si on n'a pas le prénom)
Bonjour {firstname}, (si on a le prénom)

J'ai vu que vous aidez {ICP} à {résultat} (notamment via {preuve courte : offre / cas client / wording de son site}).

On a fait des tests côté agence et on a fait remonter {X} demandes qui cherchent exactement {service} (très proches de votre positionnement sur {website_url}).

Si vous prenez encore des clients, je peux vous mettre en relation.

Vous êtes dispo 10 min {jour_ouvre01} ou {jour_ouvre02} ? Si oui à quelle heure ? Si non, à vous de me dire quand.

En vous souhaitant une agréable journée.`,
        email02: `Madame, Monsieur, (si on n'a pas le prénom)
Bonjour {firstname}, (si on a le prénom)

Je remonte ce message au cas où.

J'ai encore {X} demandes en attente d'orientation, je préfère vous les proposer car votre positionnement {info_positionnement} est tout à fait pertinent. Sinon je les transférerai ailleurs.

Si c'est un non, dites-moi juste "non" et je clôture.

Si c'est un oui, on se prend 10 min cette semaine ? Quelles sont vos disponibilités ?

En vous souhaitant une excellente journée.`,
        email03: `Madame, Monsieur, (si on n'a pas le prénom)
Bonjour {firstname}, (si on a le prénom)

Pour que ce soit concret, voilà 2 exemples anonymisés (issus des tests) :
"{phrase prospect 1 qui exprime le besoin + urgence}"
"{phrase prospect 2}"

C'est pile le type de demande que vous traitez sur {website}.

Je vous les forward si vous me confirmez que vous avez encore de la bande passante.

10 min {jour_ouvre01} ou {jour_ouvre02} ?

Dans l'attente de vous lire.`,
        fallback_email01: `Madame, Monsieur, (si on n'a pas le prénom)
Bonjour {firstname}, (si on a le prénom),

En réalisant des tests au sein de notre agence nous avons identifié une niche de clients dont les besoins correspondent exactement à votre profil d'activité.

Ces demandes sortant de notre périmètre d'expertise, j'ai pensé que cela pourrait directement vous intéresser.

Avez-vous la capacité de prendre de nouveaux clients en ce moment ? Si oui, quelles sont vos disponibilités pour en discuter rapidement ?

Bonne journée.`,
        fallback_email02: `Madame, Monsieur, (si on n'a pas le prénom)
Bonjour {firstname}, (si on a le prénom),

Je me permets de remonter mon précédent message.

Ces prospects sont toujours en attente d'un prestataire compétent. Si l'acquisition de nouveaux clients est un sujet d'actualité pour vous, êtes-vous disponible pour un appel de 5 minutes cette semaine ?

Bien à vous.`,
        fallback_email03: `Madame, Monsieur, (si on n'a pas le prénom)
Bonjour {firstname}, (si on a le prénom),

Sans retour de votre part d'ici la fin de la semaine, je proposerai ces contacts à un autre acteur de votre domaine d'activité.

Si ce n'est simplement pas le bon moment pour vous, n'hésitez pas à me le dire !

Excellente journée.`,
      },

      // W1 setters
      setChannels: (chs) => set({ channels_enabled: chs }),
      setMailThenCallFallback: (v) => set({ mail_then_call_fallback: v }),
      setFormfillRunsInParallel: (v) => set({ formfill_runs_in_parallel: v }),
      setAbandonIfNothing: (v) => set({ abandon_if_nothing: v }),

      // W2 setters
      patchW2: (patch) => set(patch),
      setMailProviders: (providers) => {
        const next = uniqProviders(providers);
        set({
          mail_providers: next,
          mail_provider: coerceLegacyMailProvider(next),
          mail_provider_email_account_ids: [],
        });
      },
      toggleMailProvider: (provider) =>
        set((state) => {
          const next = state.mail_providers.includes(provider)
            ? state.mail_providers.filter((entry) => entry !== provider)
            : [...state.mail_providers, provider];
          return {
            mail_providers: next,
            mail_provider: coerceLegacyMailProvider(next),
            mail_provider_email_account_ids: [],
          };
        }),
      setMailAccounts: (ids) => set({ mail_provider_email_account_ids: ids }),
      setOutreachAgents: (ids) => set({ outreach_agent_ids: Array.from(new Set(ids)) }),
      toggleOutreachAgent: (id) =>
        set((state) => ({
          outreach_agent_ids: state.outreach_agent_ids.includes(id)
            ? state.outreach_agent_ids.filter((entry) => entry !== id)
            : [...state.outreach_agent_ids, id],
        })),

      // W3 setters
      patchEmailTemplates: (patch) =>
        set((s) => ({ email_templates: { ...s.email_templates, ...patch } })),
      setEmailTemplates: (next) => set({ email_templates: next }),
      setEmailTemplateStyleId: (id) => set({ email_template_style_id: id }),

      // W4 setters
      setFormMessageTemplate: (v) => set({ form_message_template: v }),
      setFormMessageOptimizePerContact: (v) => set({ form_message_optimize_per_contact: v }),

      // W5 setters
      setReplyToAddress: (v) => set({ reply_to_address: v }),
      setReplyToBrandHandler: (v) => set({ reply_to_brand_handler: v }),

      // W6 mail assistant setters
      patchMailAssistantPolicy: (patch) =>
        set((state) => ({
          mail_assistant_policy: { ...state.mail_assistant_policy, ...patch },
        })),
      setMailAssistantDocs: (docs) => set({ mail_assistant_docs: docs }),
      addMailAssistantDoc: (doc) =>
        set((state) => ({ mail_assistant_docs: [...state.mail_assistant_docs, doc] })),
      updateMailAssistantDoc: (id, patch) =>
        set((state) => ({
          mail_assistant_docs: state.mail_assistant_docs.map((doc) =>
            doc.id === id ? { ...doc, ...patch } : doc,
          ),
        })),
      removeMailAssistantDoc: (id) =>
        set((state) => ({
          mail_assistant_docs: state.mail_assistant_docs.filter((doc) => doc.id !== id),
        })),

      // W6 setters
      setBigcallWorkspaceId: (v) => set({ bigcall_workspace_id: v }),
      setBigcallCampaignMode: (v) => set({ bigcall_campaign_mode: v }),
      setBigcallCampaignId: (v) => set({ bigcall_campaign_id: v }),
      setNewBigcallCampaignName: (v) => set({ new_bigcall_campaign_name: v }),

      // W8 setters
      setCampaignName: (v) => set({ campaign_name: v, campaign_name_manual_override: true }),
      syncGeneratedCampaignName: (v) =>
        set((state) =>
          state.campaign_name_manual_override && state.campaign_name.trim().length > 0
            ? state
            : { campaign_name: v, campaign_name_manual_override: false },
        ),
      useGeneratedCampaignName: (v) =>
        set({ campaign_name: v, campaign_name_manual_override: false }),
      setMailProvider: (v) => {
        const next: MailProviderKey[] = v === 'instantly' || v === 'successai' ? [v] : [];
        set({ mail_provider: v, mail_providers: next, mail_provider_email_account_ids: [] });
      },

      // W7 setters
      setEnrichmentProviders: (v) =>
        set({ enrichment_providers: uniqEnrichmentProviders(v) }),
      toggleEnrichmentProvider: (id) =>
        set((s) => ({
          enrichment_providers: s.enrichment_providers.includes(id)
            ? s.enrichment_providers.filter((p) => p !== id)
            : uniqEnrichmentProviders([...s.enrichment_providers, id]),
        })),
      setEnrichmentForceComplete: (v) => set({ enrichment_force_complete: v }),
      setLeadScrapeTargeting: (v) => set({ leadscrape_targeting: v }),
      setCsvImportDraft: (v) => set({ csv_import_draft: v }),
      patchFitGate: (patch) => set((s) => ({ fit_gate: { ...s.fit_gate, ...patch } })),
    }),
    {
      name: 'dispatcher-wizard-store',
      version: 6,
      partialize: ({ csv_import_draft: _csv_import_draft, ...state }) => state,
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as Partial<WizardState> & {
          mail_provider?: string;
          mail_providers?: string[];
          enrichment_providers?: string[];
        };

        const enrichmentProviders = Array.isArray(state.enrichment_providers)
          ? uniqEnrichmentProviders(state.enrichment_providers)
          : ['leadscrape'];
        const outreachAgentIds = Array.isArray(state.outreach_agent_ids)
          ? state.outreach_agent_ids.filter((value): value is string => typeof value === 'string')
          : [];

        if (Array.isArray(state.mail_providers)) {
          return {
            ...state,
            mail_providers: uniqProviders(state.mail_providers),
            mail_provider:
              state.mail_provider ?? coerceLegacyMailProvider(uniqProviders(state.mail_providers)),
            campaign_identifier: state.campaign_identifier ?? '',
            campaign_name_manual_override: state.campaign_name_manual_override ?? false,
            mail_assistant_policy: state.mail_assistant_policy ?? defaultMailAssistantPolicy(),
            mail_assistant_docs: state.mail_assistant_docs ?? [],
            outreach_agent_ids: outreachAgentIds,
            enrichment_providers: enrichmentProviders,
            csv_import_draft: null,
          } as WizardState;
        }

        const fallbackProviders: MailProviderKey[] =
          state.mail_provider === 'instantly' || state.mail_provider === 'successai'
            ? [state.mail_provider]
            : [];

        return {
          ...state,
          mail_providers: fallbackProviders,
          mail_provider: state.mail_provider ?? coerceLegacyMailProvider(fallbackProviders),
          campaign_identifier: state.campaign_identifier ?? '',
          campaign_name_manual_override: state.campaign_name_manual_override ?? false,
          mail_assistant_policy: state.mail_assistant_policy ?? defaultMailAssistantPolicy(),
          mail_assistant_docs: state.mail_assistant_docs ?? [],
          outreach_agent_ids: outreachAgentIds,
          enrichment_providers: enrichmentProviders,
          csv_import_draft: null,
        } as WizardState;
      },
    },
  ),
);
